import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, Trash2, Copy, ArrowUp, ArrowDown, Send, Download, Eye, CheckCircle2, ClipboardList,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  PageTitle, Card, SectionTitle, Takeaway, LoadingBlocks, EmptyState,
} from "@/components/shared/primitives";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { Tabs } from "@/components/kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const EDIT_ROLES = ["admin", "it_admin", "team_lead"];
const QTYPES = [
  { value: "mc_single", label: "Multiple choice (single)" },
  { value: "mc_multi", label: "Multiple choice (multiple)" },
  { value: "poll", label: "Poll" },
  { value: "dropdown", label: "Dropdown" },
  { value: "yes_no", label: "Yes / No" },
  { value: "rating", label: "Rating (1 to 5)" },
  { value: "short_text", label: "Short text" },
  { value: "long_text", label: "Long text" },
];
const HAS_OPTIONS = ["mc_single", "mc_multi", "poll", "dropdown"];
const ROLE_OPTS = [
  { value: "admin", label: "Admin / KM" },
  { value: "team_lead", label: "Partner" },
  { value: "member", label: "Member" },
];
const STATUS_LABEL = { draft: "Draft", scheduled: "Scheduled", live: "Live", closed: "Closed" };

const uid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()));
const blankQuestion = () => ({ id: uid(), type: "mc_single", text: "", options: ["", ""], required: false });

function StatusBadge({ status }) {
  const bg = { live: "var(--brand-kawaii)", draft: "var(--bg-sunken)", scheduled: "var(--brand-floral)", closed: "var(--border-control)" }[status] || "var(--bg-sunken)";
  return <span className="inline-flex items-center gap-1.5 text-[12px] text-nb-ink rounded-md px-2 py-0.5" style={{ background: "var(--bg-sunken)" }}>
    <span className="w-1.5 h-1.5 rounded-full" style={{ background: bg }} />{STATUS_LABEL[status] || status}</span>;
}

function ProgressBar({ value }) {
  return <div className="h-2 rounded-full overflow-hidden" style={{ background: "var(--bg-sunken)" }}>
    <div className="h-full rounded-full" style={{ width: `${Math.round((value || 0) * 100)}%`, background: "var(--brand-kawaii)" }} />
  </div>;
}

/* ---------------- Builder ---------------- */
function Builder({ open, onClose, onSaved }) {
  const [form, setForm] = useState({ title: "", description: "", anonymous: false, reminders: false, due_date: "", scheduled_for: "", recipient_type: "everyone", recipient_ids: [], questions: [blankQuestion()] });
  const [teams, setTeams] = useState([]);
  const [preview, setPreview] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (open) {
      setForm({ title: "", description: "", anonymous: false, reminders: false, due_date: "", scheduled_for: "", recipient_type: "everyone", recipient_ids: [], questions: [blankQuestion()] });
      setPreview(false);
      api.get("/teams").then((r) => setTeams((r.data.rows || []).map((t) => ({ id: t.team_id, name: t.team })))).catch(() => {});
    }
  }, [open]);

  const updQ = (i, patch) => setForm((f) => ({ ...f, questions: f.questions.map((q, idx) => idx === i ? { ...q, ...patch } : q) }));
  const addQ = () => setForm((f) => ({ ...f, questions: [...f.questions, blankQuestion()] }));
  const dupQ = (i) => setForm((f) => { const c = { ...f.questions[i], id: uid() }; const arr = [...f.questions]; arr.splice(i + 1, 0, c); return { ...f, questions: arr }; });
  const delQ = (i) => setForm((f) => ({ ...f, questions: f.questions.filter((_, idx) => idx !== i) }));
  const moveQ = (i, d) => setForm((f) => { const arr = [...f.questions]; const j = i + d; if (j < 0 || j >= arr.length) return f; [arr[i], arr[j]] = [arr[j], arr[i]]; return { ...f, questions: arr }; });

  const toggleRecipient = (id) => set("recipient_ids", form.recipient_ids.includes(id) ? form.recipient_ids.filter((x) => x !== id) : [...form.recipient_ids, id]);

  const payload = () => ({
    ...form,
    questions: form.questions.map((q) => ({
      id: q.id, type: q.type, text: q.text, required: q.required,
      options: q.type === "yes_no" ? ["Yes", "No"] : (HAS_OPTIONS.includes(q.type) ? q.options.filter((o) => o.trim()) : []),
    })),
  });

  const validate = () => {
    if (!form.title.trim()) { toast.error("Add a title."); return false; }
    if (form.questions.some((q) => !q.text.trim())) { toast.error("Every question needs text."); return false; }
    return true;
  };

  const save = async (send) => {
    if (!validate()) return;
    try {
      const res = await api.post("/questionnaires", payload());
      if (send) await api.post(`/questionnaires/${res.data.id}/send`);
      toast.success(send ? "Questionnaire sent." : "Draft saved.");
      onSaved();
      onClose();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="bg-nb-surface w-[560px] sm:max-w-[560px] overflow-y-auto">
        <SheetHeader><SheetTitle className="text-nb-ink font-display">New questionnaire</SheetTitle></SheetHeader>

        {!preview ? (
          <div className="space-y-5 mt-6">
            <div><Label className="text-[13px]">Title</Label><Input data-testid="q-title" value={form.title} onChange={(e) => set("title", e.target.value)} className="mt-1" placeholder="e.g. AI tools pulse check" /></div>
            <div><Label className="text-[13px]">Description (optional)</Label><Textarea value={form.description} onChange={(e) => set("description", e.target.value)} className="mt-1" rows={2} /></div>

            <div className="space-y-3">
              {form.questions.map((q, i) => (
                <div key={q.id} className="rounded-xl p-3" style={{ border: "1px solid var(--border-subtle)" }} data-testid={`q-item-${i}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="nb-label">Q{i + 1}</span>
                    <div className="ml-auto flex items-center gap-1">
                      <button onClick={() => moveQ(i, -1)} className="p-1 text-nb-ink2 hover:text-nb-ink" title="Move up"><ArrowUp size={15} /></button>
                      <button onClick={() => moveQ(i, 1)} className="p-1 text-nb-ink2 hover:text-nb-ink" title="Move down"><ArrowDown size={15} /></button>
                      <button onClick={() => dupQ(i)} className="p-1 text-nb-ink2 hover:text-nb-ink" title="Duplicate" data-testid={`q-dup-${i}`}><Copy size={15} /></button>
                      <button onClick={() => delQ(i)} className="p-1 text-nb-ink2 hover:text-nb-ink" title="Delete" data-testid={`q-del-${i}`}><Trash2 size={15} /></button>
                    </div>
                  </div>
                  <Input value={q.text} onChange={(e) => updQ(i, { text: e.target.value })} placeholder="Question text" className="mb-2" data-testid={`q-text-${i}`} />
                  <div className="flex items-center gap-2 flex-wrap">
                    <Select value={q.type} onValueChange={(v) => updQ(i, { type: v, options: HAS_OPTIONS.includes(v) && (!q.options || !q.options.length) ? ["", ""] : q.options })}>
                      <SelectTrigger className="h-9 w-[220px]" data-testid={`q-type-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{QTYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                    </Select>
                    <label className="flex items-center gap-2 text-[13px] text-nb-ink2 ml-auto">
                      <Switch checked={q.required} onCheckedChange={(v) => updQ(i, { required: v })} /> Required
                    </label>
                  </div>
                  {HAS_OPTIONS.includes(q.type) && (
                    <div className="mt-2 space-y-1.5">
                      {q.options.map((o, oi) => (
                        <div key={oi} className="flex items-center gap-2">
                          <Input value={o} onChange={(e) => updQ(i, { options: q.options.map((x, xi) => xi === oi ? e.target.value : x) })} placeholder={`Option ${oi + 1}`} className="h-9" />
                          <button onClick={() => updQ(i, { options: q.options.filter((_, xi) => xi !== oi) })} className="p-1 text-nb-ink2 hover:text-nb-ink"><Trash2 size={14} /></button>
                        </div>
                      ))}
                      <button onClick={() => updQ(i, { options: [...q.options, ""] })} className="text-[13px]" style={{ color: "var(--text-link)" }}>+ Add option</button>
                    </div>
                  )}
                  {q.type === "rating" && <p className="text-[12px] text-nb-ink2 mt-2">Respondents pick 1 to 5.</p>}
                  {q.type === "yes_no" && <p className="text-[12px] text-nb-ink2 mt-2">Respondents pick Yes or No.</p>}
                </div>
              ))}
              <Button variant="outline" onClick={addQ} className="rounded-[10px] gap-2 w-full" data-testid="q-add"><Plus size={16} /> Add question</Button>
            </div>

            <div className="pt-2 border-t" style={{ borderColor: "var(--border-subtle)" }}>
              <SectionTitle className="mb-2 text-[16px]">Recipients</SectionTitle>
              <Select value={form.recipient_type} onValueChange={(v) => set("recipient_type", v) || set("recipient_ids", [])}>
                <SelectTrigger className="h-10" data-testid="q-recipient-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="everyone">Everyone</SelectItem>
                  <SelectItem value="teams">Specific teams</SelectItem>
                  <SelectItem value="roles">Specific roles</SelectItem>
                </SelectContent>
              </Select>
              {form.recipient_type === "teams" && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {teams.map((t) => (
                    <button key={t.id} onClick={() => toggleRecipient(t.id)} className="text-[13px] px-2.5 py-1 rounded-md" style={form.recipient_ids.includes(t.id) ? { background: "var(--brand-lime)", color: "var(--brand-navy)" } : { border: "1px solid var(--border-subtle)" }}>{t.name}</button>
                  ))}
                </div>
              )}
              {form.recipient_type === "roles" && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {ROLE_OPTS.map((t) => (
                    <button key={t.value} onClick={() => toggleRecipient(t.value)} className="text-[13px] px-2.5 py-1 rounded-md" style={form.recipient_ids.includes(t.value) ? { background: "var(--brand-lime)", color: "var(--brand-navy)" } : { border: "1px solid var(--border-subtle)" }}>{t.label}</button>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-[13px]">Due date</Label><Input type="date" value={form.due_date} onChange={(e) => set("due_date", e.target.value)} className="mt-1" /></div>
              <div><Label className="text-[13px]">Schedule send (optional)</Label><Input type="datetime-local" value={form.scheduled_for} onChange={(e) => set("scheduled_for", e.target.value)} className="mt-1" /></div>
            </div>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 text-[14px] text-nb-ink"><Switch checked={form.anonymous} onCheckedChange={(v) => set("anonymous", v)} data-testid="q-anon" /> Anonymous responses</label>
              <label className="flex items-center gap-2 text-[14px] text-nb-ink"><Switch checked={form.reminders} onCheckedChange={(v) => set("reminders", v)} /> Send reminders</label>
            </div>
          </div>
        ) : (
          <div className="space-y-5 mt-6" data-testid="q-preview">
            <h3 className="font-display text-[20px] font-bold text-nb-ink">{form.title || "Untitled"}</h3>
            {form.description && <p className="text-[14px] text-nb-ink2">{form.description}</p>}
            {form.questions.map((q, i) => <RespondQuestion key={q.id} q={{ ...q, options: q.type === "yes_no" ? ["Yes", "No"] : q.options }} index={i} value={undefined} onChange={() => {}} disabled />)}
          </div>
        )}

        <div className="flex items-center gap-2 mt-8">
          <Button variant="ghost" onClick={() => setPreview((p) => !p)} className="gap-2"><Eye size={16} /> {preview ? "Back to edit" : "Preview"}</Button>
          <div className="ml-auto flex gap-2">
            <Button variant="outline" onClick={() => save(false)} className="rounded-[10px]" data-testid="q-save-draft">Save draft</Button>
            <Button onClick={() => save(true)} className="rounded-[10px] bg-nb-navy text-nb-cream font-semibold gap-2" data-testid="q-create-send"><Send size={15} /> Create and send</Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

/* ---------------- Respond ---------------- */
function RespondQuestion({ q, index, value, onChange, disabled }) {
  const opts = q.options || [];
  return (
    <div className="rounded-xl p-4" style={{ border: "1px solid var(--border-subtle)" }} data-testid={`respond-q-${index}`}>
      <div className="text-[14px] font-medium text-nb-ink mb-3">{index + 1}. {q.text} {q.required && <span style={{ color: "var(--neg-text)" }}>*</span>}</div>
      {(q.type === "mc_single" || q.type === "poll" || q.type === "yes_no") && (
        <div className="space-y-2">
          {opts.map((o) => (
            <label key={o} className="flex items-center gap-2 text-[14px] text-nb-ink cursor-pointer">
              <input type="radio" name={q.id} checked={value === o} disabled={disabled} onChange={() => onChange(o)} /> {o}
            </label>
          ))}
        </div>
      )}
      {q.type === "mc_multi" && (
        <div className="space-y-2">
          {opts.map((o) => {
            const arr = Array.isArray(value) ? value : [];
            return <label key={o} className="flex items-center gap-2 text-[14px] text-nb-ink cursor-pointer">
              <input type="checkbox" checked={arr.includes(o)} disabled={disabled} onChange={() => onChange(arr.includes(o) ? arr.filter((x) => x !== o) : [...arr, o])} /> {o}
            </label>;
          })}
        </div>
      )}
      {q.type === "dropdown" && (
        <Select value={value || ""} onValueChange={onChange} disabled={disabled}>
          <SelectTrigger className="h-10 w-full"><SelectValue placeholder="Choose" /></SelectTrigger>
          <SelectContent>{opts.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
        </Select>
      )}
      {q.type === "rating" && (
        <div className="flex gap-2">
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n} disabled={disabled} onClick={() => onChange(String(n))} className="w-10 h-10 rounded-[10px] text-[15px] font-semibold" style={String(value) === String(n) ? { background: "var(--brand-kawaii)", color: "var(--brand-navy)" } : { border: "1px solid var(--border-control)", color: "var(--text-primary)" }}>{n}</button>
          ))}
        </div>
      )}
      {q.type === "short_text" && <Input value={value || ""} disabled={disabled} onChange={(e) => onChange(e.target.value)} />}
      {q.type === "long_text" && <Textarea rows={3} value={value || ""} disabled={disabled} onChange={(e) => onChange(e.target.value)} />}
    </div>
  );
}

function RespondSheet({ q, open, onClose, onDone }) {
  const [detail, setDetail] = useState(null);
  const [answers, setAnswers] = useState({});
  useEffect(() => {
    if (open && q) { setAnswers({}); api.get(`/questionnaires/${q.id}`).then((r) => setDetail(r.data)); }
  }, [open, q]);
  const submit = async () => {
    try { await api.post(`/questionnaires/${q.id}/respond`, { answers }); toast.success("Thanks, your response was recorded."); onDone(); onClose(); }
    catch (e) { toast.error(e.message); }
  };
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="bg-nb-surface w-[560px] sm:max-w-[560px] overflow-y-auto">
        <SheetHeader><SheetTitle className="text-nb-ink font-display">{q?.title}</SheetTitle></SheetHeader>
        {!detail ? <LoadingBlocks rows={3} /> : (
          <div className="space-y-4 mt-6">
            {detail.anonymous && <div className="text-[13px] rounded-xl p-3 text-nb-ink" style={{ background: "rgba(146,186,213,0.22)" }}>Your answers are anonymous.</div>}
            {detail.questions.map((qq, i) => <RespondQuestion key={qq.id} q={qq} index={i} value={answers[qq.id]} onChange={(v) => setAnswers((a) => ({ ...a, [qq.id]: v }))} />)}
            <Button onClick={submit} className="w-full rounded-[10px] bg-nb-navy text-nb-cream font-semibold" data-testid="respond-submit">Submit response</Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

/* ---------------- Responses viewer ---------------- */
function ResponsesSheet({ q, open, onClose }) {
  const [tab, setTab] = useState("summary");
  const [summary, setSummary] = useState(null);
  const [individual, setIndividual] = useState(null);
  useEffect(() => {
    if (open && q) {
      setTab("summary");
      api.get(`/questionnaires/${q.id}/summary`).then((r) => setSummary(r.data)).catch(() => setSummary(null));
      api.get(`/questionnaires/${q.id}/responses`).then((r) => setIndividual(r.data)).catch(() => setIndividual(null));
    }
  }, [open, q]);
  const exportCsv = async () => {
    try {
      const res = await api.raw.get(`/questionnaires/${q.id}/export`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a");
      a.href = url; a.download = `${q.title}.csv`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Export failed."); }
  };
  const maxCount = (d) => Math.max(1, ...(d || []).map((x) => x.count));
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="bg-nb-surface w-[620px] sm:max-w-[620px] overflow-y-auto">
        <SheetHeader><SheetTitle className="text-nb-ink font-display">{q?.title}</SheetTitle></SheetHeader>
        <div className="flex items-center gap-2 mt-4">
          {["summary", "individual"].map((t) => (
            <button key={t} onClick={() => setTab(t)} className="px-3 py-1.5 rounded-md text-[13px] font-medium capitalize" style={tab === t ? { background: "var(--brand-lime)", color: "var(--brand-navy)" } : { border: "1px solid var(--border-subtle)" }}>{t}</button>
          ))}
          <Button variant="outline" onClick={exportCsv} className="ml-auto rounded-[10px] gap-2 h-9" data-testid="q-export"><Download size={14} /> CSV</Button>
        </div>

        {tab === "summary" && (summary ? (
          summary.group_too_small ? <p className="mt-6 text-[14px] text-nb-ink2 italic">Not enough people to show.</p> : (
            <div className="mt-5 space-y-5">
              <p className="text-[13px] text-nb-ink2">{summary.total} response{summary.total === 1 ? "" : "s"}{summary.anonymous ? " · anonymous" : ""}</p>
              {summary.questions.map((qq) => (
                <div key={qq.id}>
                  <div className="text-[14px] font-medium text-nb-ink mb-2">{qq.text}</div>
                  {qq.type === "rating" && <p className="text-[13px] text-nb-ink2 mb-2">Average: <strong className="text-nb-ink tnum">{qq.average}</strong> / 5</p>}
                  {qq.distribution ? (
                    <div className="space-y-1.5">
                      {qq.distribution.map((d) => (
                        <div key={d.label} className="flex items-center gap-2">
                          <span className="w-28 text-[12px] text-nb-ink2 truncate">{d.label}</span>
                          <span className="flex-1 h-4 rounded-md overflow-hidden" style={{ background: "var(--bg-sunken)" }}>
                            <span className="block h-full rounded-md" style={{ width: `${(d.count / maxCount(qq.distribution)) * 100}%`, background: "var(--brand-kawaii)" }} />
                          </span>
                          <span className="w-8 text-right text-[12px] text-nb-ink2 tnum">{d.count}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <ul className="space-y-1">
                      {(qq.answers || []).length === 0 ? <li className="text-[13px] text-nb-ink2">No answers yet.</li> :
                        qq.answers.map((a, i) => <li key={i} className="text-[13px] text-nb-ink rounded-md px-2.5 py-1.5" style={{ background: "var(--bg-sunken)" }}>{a}</li>)}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )
        ) : <LoadingBlocks rows={3} />)}

        {tab === "individual" && (individual ? (
          individual.anonymous ? <p className="mt-6 text-[14px] text-nb-ink2 italic">This questionnaire is anonymous, individual answers are not shown.</p> : (
            <div className="mt-5 space-y-3">
              {individual.rows.length === 0 ? <p className="text-[14px] text-nb-ink2">No responses yet.</p> : individual.rows.map((r, i) => (
                <Card key={i} className="p-4">
                  <div className="flex justify-between text-[13px] mb-2"><span className="font-semibold text-nb-ink">{r.respondent}</span><span className="text-nb-ink2">{r.created_at?.slice(0, 10)}</span></div>
                  {individual.questions.map((qq) => (
                    <div key={qq.id} className="text-[13px] mb-1"><span className="text-nb-ink2">{qq.text}: </span><span className="text-nb-ink">{Array.isArray(r.answers[qq.id]) ? r.answers[qq.id].join(", ") : (r.answers[qq.id] || "-")}</span></div>
                  ))}
                </Card>
              ))}
            </div>
          )
        ) : <LoadingBlocks rows={3} />)}
      </SheetContent>
    </Sheet>
  );
}

/* ---------------- Grievances / tickets ---------------- */
const GRIEVANCE_CATEGORIES = ["General", "Access", "Training", "Performance", "Billing", "Tool request", "Other"];
const GRIEVANCE_STATUSES = ["Open", "In progress", "Resolved", "Closed"];
const G_PRIORITY_DOT = { High: "var(--neg-text)", Medium: "var(--brand-floral)", Low: "var(--text-muted)" };
const G_STATUS_DOT = { Open: "var(--brand-kawaii)", "In progress": "var(--brand-floral)", Resolved: "var(--pos-text)", Closed: "var(--border-control)" };

function GChip({ text, dot }) {
  return <span className="inline-flex items-center gap-1.5 text-[12.5px] text-nb-ink rounded-md px-2 py-0.5" style={{ background: "var(--bg-sunken)" }}><span className="w-1.5 h-1.5 rounded-full" style={{ background: dot }} />{text}</span>;
}

function RaiseTicketSheet({ open, onClose, onSaved }) {
  const [form, setForm] = useState({ subject: "", category: "General", priority: "Medium" });
  useEffect(() => { if (open) setForm({ subject: "", category: "General", priority: "Medium" }); }, [open]);
  const submit = async () => {
    if (!form.subject.trim()) { toast.error("Add a short summary of the issue."); return; }
    try {
      await api.post("/grievances", form);
      toast.success("Ticket raised. The AI team will take a look.");
      onSaved(); onClose();
    } catch (e) { toast.error(e.message); }
  };
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="bg-nb-surface w-[480px] sm:max-w-[480px] overflow-y-auto">
        <SheetHeader><SheetTitle className="text-nb-ink font-display">Raise a ticket</SheetTitle>
          <SheetDescription className="text-nb-ink2">Flag a problem with an AI tool or training. Admins and Partners will follow up.</SheetDescription>
        </SheetHeader>
        <div className="space-y-5 mt-6">
          <div><Label className="text-[13px]">What's the issue?</Label><Input data-testid="ticket-subject" value={form.subject} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))} className="mt-1" placeholder="e.g. CoCounsel keeps timing out" /></div>
          <div>
            <Label className="text-[13px]">Category</Label>
            <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
              <SelectTrigger className="mt-1 h-10" data-testid="ticket-category"><SelectValue /></SelectTrigger>
              <SelectContent>{GRIEVANCE_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[13px]">Priority</Label>
            <Select value={form.priority} onValueChange={(v) => setForm((f) => ({ ...f, priority: v }))}>
              <SelectTrigger className="mt-1 h-10" data-testid="ticket-priority"><SelectValue /></SelectTrigger>
              <SelectContent>{["High", "Medium", "Low"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button onClick={submit} className="w-full rounded-[10px] bg-nb-navy text-nb-cream font-semibold" data-testid="ticket-submit">Raise ticket</Button>
          <p className="text-[12px] text-nb-ink2">Tickets are about AI tools and training only. Never include confidential client or matter detail.</p>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function GrievancesTab({ isEditor, onRaise }) {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => api.get("/grievances").then((r) => setRows(r.data)).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);
  // expose a reload to parent raise flow
  useEffect(() => { onRaise.current = load; }, [onRaise, load]);

  const setStatus = async (id, status) => {
    try { await api.patch(`/grievances/${id}`, { status }); load(); }
    catch (e) { toast.error(e.message); }
  };

  if (rows === null) return <LoadingBlocks rows={3} />;
  if (rows.length === 0) return <EmptyState title="No tickets yet." next={isEditor ? "Tickets raised by your people will appear here." : "Raise a ticket if an AI tool is not working for you."} />;

  return (
    <Card className="p-5" data-testid="grievances-list">
      <div className="overflow-x-auto">
        <table className="w-full text-[14px]">
          <thead><tr className="nb-label text-left" style={{ background: "var(--bg-sunken)" }}>
            <th className="py-2.5 px-3 first:rounded-l-md">Issue</th>
            {isEditor && <th className="py-2.5 px-3">Raised by</th>}
            <th className="py-2.5 px-3">Category</th>
            <th className="py-2.5 px-3">Priority</th>
            <th className="py-2.5 px-3">Raised</th>
            <th className="py-2.5 px-3 last:rounded-r-md">Status</th>
          </tr></thead>
          <tbody>
            {rows.map((g) => (
              <tr key={g.id} className="border-b last:border-0" style={{ borderColor: "var(--border-subtle)" }} data-testid={`grievance-row-${g.id}`}>
                <td className="py-3 px-3 text-nb-ink font-medium">{g.subject}</td>
                {isEditor && <td className="py-3 px-3 text-nb-ink2">{g.member_name}{g.team ? ` · ${g.team}` : ""}</td>}
                <td className="py-3 px-3 text-nb-ink2">{g.category}</td>
                <td className="py-3 px-3"><GChip text={g.priority} dot={G_PRIORITY_DOT[g.priority]} /></td>
                <td className="py-3 px-3 text-nb-ink2 tnum">{g.created_at?.slice(0, 10)}</td>
                <td className="py-3 px-3">
                  {isEditor ? (
                    <Select value={g.status} onValueChange={(v) => setStatus(g.id, v)}>
                      <SelectTrigger className="h-9 w-[150px]" data-testid={`grievance-status-${g.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{GRIEVANCE_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : (
                    <GChip text={g.status} dot={G_STATUS_DOT[g.status]} />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/* ---------------- Page ---------------- */
export default function Review() {
  const { role, member } = useAuth();
  const isEditor = EDIT_ROLES.includes(role);
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") || (isEditor ? "questionnaires" : "tasks");
  const setTab = (t) => { const n = new URLSearchParams(sp); n.set("tab", t); setSp(n); };

  const [list, setList] = useState(null);
  const [mine, setMine] = useState(null);
  const [builder, setBuilder] = useState(false);
  const [respond, setRespond] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [raising, setRaising] = useState(false);
  const grievancesReload = useRef(() => {});

  const loadList = useCallback(() => { if (isEditor) api.get("/questionnaires").then((r) => setList(r.data)).catch(() => setList([])); }, [isEditor]);
  const loadMine = useCallback(() => api.get("/questionnaires/mine").then((r) => setMine(r.data)).catch(() => setMine([])), []);
  useEffect(() => { loadList(); loadMine(); }, [loadList, loadMine]);

  const sendNow = async (id) => { try { await api.post(`/questionnaires/${id}/send`); toast.success("Sent."); loadList(); } catch (e) { toast.error(e.message); } };
  const closeQ = async (id) => { try { await api.post(`/questionnaires/${id}/close`); loadList(); } catch (e) { toast.error(e.message); } };
  const deleteQ = async () => {
    try { await api.del(`/questionnaires/${deleting.id}`); toast.success("Questionnaire deleted."); loadList(); }
    catch (e) { toast.error(e.message); }
    setDeleting(null);
  };
  const canDelete = (q) => role === "admin" || q.created_by === member?.id;

  const tabs = [];
  if (isEditor) tabs.push({ key: "questionnaires", label: "Questionnaires" });
  tabs.push({ key: "tasks", label: "My tasks" });
  tabs.push({ key: "grievances", label: "Tickets" });

  return (
    <div>
      <PageTitle
        testid="review-page"
        actions={
          isEditor && tab === "questionnaires"
            ? <Button onClick={() => setBuilder(true)} data-testid="new-questionnaire" className="rounded-[10px] bg-nb-navy text-nb-cream font-semibold gap-2"><Plus size={16} /> New questionnaire</Button>
            : tab === "grievances"
              ? <Button onClick={() => setRaising(true)} data-testid="raise-ticket" className="rounded-[10px] bg-nb-navy text-nb-cream font-semibold gap-2"><Plus size={16} /> Raise a ticket</Button>
              : null
        }
      >Review</PageTitle>
      <Takeaway>Questionnaires capture what usage numbers cannot show. Tickets flag tool and training problems people hit.</Takeaway>

      <div className="mb-6">
        <Tabs tabs={tabs} value={tab} onChange={setTab} testid="review-tabs" />
      </div>

      {tab === "questionnaires" && isEditor && (
        list === null ? <LoadingBlocks rows={3} /> :
        list.length === 0 ? <EmptyState title="No questionnaires yet." next="Create one to start gathering feedback." action={<Button onClick={() => setBuilder(true)} className="rounded-[10px] bg-nb-navy text-nb-cream font-semibold gap-2"><Plus size={16} /> New questionnaire</Button>} /> : (
          <Card className="p-5" data-testid="q-list">
            <div className="overflow-x-auto">
              <table className="w-full text-[14px]">
                <thead><tr className="nb-label text-left" style={{ background: "var(--bg-sunken)" }}>
                  <th className="py-2.5 px-3 first:rounded-l-md">Title</th><th className="py-2.5 px-3">Status</th>
                  <th className="py-2.5 px-3 text-right">Recipients</th><th className="py-2.5 px-3 w-[200px]">Response rate</th>
                  <th className="py-2.5 px-3">Due</th><th className="py-2.5 px-3 last:rounded-r-md">Actions</th>
                </tr></thead>
                <tbody>
                  {list.map((q) => (
                    <tr key={q.id} className="border-b last:border-0" style={{ borderColor: "var(--border-subtle)" }} data-testid={`q-row-${q.id}`}>
                      <td className="py-3 px-3"><button className="text-nb-ink font-medium hover:text-[var(--text-link)] text-left" onClick={() => setViewing(q)}>{q.title}</button></td>
                      <td className="py-3 px-3"><StatusBadge status={q.status} /></td>
                      <td className="py-3 px-3 tnum text-right">{q.recipients}</td>
                      <td className="py-3 px-3"><div className="flex items-center gap-2"><div className="flex-1"><ProgressBar value={q.response_rate} /></div><span className="text-[12px] text-nb-ink2 tnum w-20 text-right">{q.responses}/{q.recipients} · {Math.round(q.response_rate * 100)}%</span></div></td>
                      <td className="py-3 px-3 text-nb-ink2">{q.due_date || "-"}</td>
                      <td className="py-3 px-3">
                        <div className="flex items-center gap-2">
                          {q.status === "draft" && <button onClick={() => sendNow(q.id)} className="text-[13px] font-medium" style={{ color: "var(--text-link)" }} data-testid={`q-send-${q.id}`}>Send</button>}
                          {q.status === "live" && <button onClick={() => closeQ(q.id)} className="text-[13px] text-nb-ink2 hover:text-nb-ink">Close</button>}
                          <button onClick={() => setViewing(q)} className="text-[13px] text-nb-ink2 hover:text-nb-ink">Responses</button>
                          {canDelete(q) && <button onClick={() => setDeleting(q)} data-testid={`q-delete-${q.id}`} className="text-nb-ink2 hover:text-[var(--neg-text)] inline-flex" title="Delete"><Trash2 size={14} /></button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )
      )}

      {tab === "tasks" && (
        mine === null ? <LoadingBlocks rows={3} /> :
        mine.length === 0 ? <EmptyState title="Nothing to fill in." next="Questionnaires sent to you will appear here." /> : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="my-tasks">
            {mine.map((q) => (
              <Card key={q.id} className="p-5 flex flex-col gap-3">
                <div className="flex items-start gap-3">
                  <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: "var(--brand-kawaii)", color: "var(--brand-navy)" }}><ClipboardList size={18} /></span>
                  <div className="min-w-0">
                    <div className="text-[15px] font-semibold text-nb-ink leading-tight">{q.title}</div>
                    {q.description && <div className="text-[13px] text-nb-ink2 line-clamp-2">{q.description}</div>}
                    {q.due_date && <div className="text-[12px] text-nb-ink2 mt-1">Due {q.due_date}</div>}
                  </div>
                </div>
                {q.completed ? (
                  <span className="inline-flex items-center gap-1.5 text-[13px] mt-auto" style={{ color: "var(--pos-text)" }}><CheckCircle2 size={16} /> Completed</span>
                ) : (
                  <Button onClick={() => setRespond(q)} disabled={q.status === "closed"} className="rounded-[10px] bg-nb-navy text-nb-cream font-semibold mt-auto" data-testid={`respond-${q.id}`}>{q.status === "closed" ? "Closed" : "Respond"}</Button>
                )}
              </Card>
            ))}
          </div>
        )
      )}

      {tab === "grievances" && (
        <GrievancesTab isEditor={isEditor} onRaise={grievancesReload} />
      )}

      <Builder open={builder} onClose={() => setBuilder(false)} onSaved={loadList} />
      <RaiseTicketSheet open={raising} onClose={() => setRaising(false)} onSaved={() => grievancesReload.current()} />
      <RespondSheet q={respond} open={!!respond} onClose={() => setRespond(null)} onDone={loadMine} />
      <ResponsesSheet q={viewing} open={!!viewing} onClose={() => setViewing(null)} />
      <ConfirmDialog
        open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}
        title={`Delete "${deleting?.title}"?`}
        description="This permanently removes the questionnaire and every response collected so far. This cannot be undone."
        confirmLabel="Delete" onConfirm={deleteQ}
      />
    </div>
  );
}
